import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  computed,
  input,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, map, startWith } from 'rxjs';

import { List } from 'models';
import { ListsService } from 'src/app/state/lists/lists.service';

import {
  MatAutocompleteActivatedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { assertExists } from 'common';
import { filterList } from 'src/app/common/utility';
import { FieldBaseComponent } from '../field-component.base.component';
import { ListFieldBaseComponent } from '../list-field-base.component';

@UntilDestroy()
@Component({
  selector: 'app-typeahead-field',
  templateUrl: './typeahead-field.component.html',
  styleUrls: ['./typeahead-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TypeaheadFieldComponent
  extends FieldBaseComponent
  implements OnInit, ListFieldBaseComponent
{
  /**
   * Alternate label for the mat-form-field.
   *
   * The field.name will be used if this is not provided.
   */
  @Input()
  alternateFormLabel: string;
  /** Autocomplete trigger. */
  @ViewChild(MatAutocompleteTrigger)
  autocompleteTrigger: MatAutocompleteTrigger;
  /** Input element reference. */
  @ViewChild('input')
  inputElement: ElementRef;
  /** Class or an array of classes to be applied to the autocomplete panel. */
  panelClass = input<string | string[]>();
  /** Class string to be added to the mat-autocomplete class attribute. */
  autoCompleteClass = computed(() => {
    const panelClass = this.panelClass();
    if (!panelClass) return ''; // There is no class so just return an empty string.

    // There is a panel class. Now see if it's an array.
    if (Array.isArray(panelClass)) {
      // It is an array so join it with a space for a typical class directive.
      return panelClass.join(' ');
    }

    // The panelClass is a string so just return it.
    return panelClass;
  });

  /** Filtered list values. */
  filteredListValues$: Observable<string[]>;
  /** Field list. */
  list: List;

  private currentlyActiveListValue?: string;

  constructor(
    translate: TranslocoService,
    private lists: ListsService
  ) {
    super(translate);
  }

  /**
   * Get the label for the mat-form-field.
   *
   * @returns The label string for the mat-form-field
   */
  get fieldLabel(): string {
    return this.alternateFormLabel || this.field().name;
  }

  /** @inheritdoc */
  closeListPanel(): void {
    this.autocompleteTrigger.closePanel();
  }

  /** @inheritdoc */
  focus(): void {
    this.inputElement.nativeElement.focus();
    this.inputElement.nativeElement.select();
  }

  ngOnInit(): void {
    this.addValidators();
    this.listenForList();
    this.filteredListValues$ = this.control().valueChanges.pipe(
      startWith(''),
      map((value) => filterList(value, this.list?.values ?? [], 100))
    );
  }

  /**
   * Handler for the auto complete's active list item event.
   *
   * @param event AUtocomplete option activation event.
   */
  onActivateListItem(event: MatAutocompleteActivatedEvent): void {
    this.currentlyActiveListValue = event.option?.value;
  }

  /** @inheritdoc */
  onBlur(event: FocusEvent): void {
    if (this.currentlyActiveListValue) {
      this.control().setValue(this.currentlyActiveListValue);
    }

    if (!this.shouldBlur(event)) return;
    this.fieldBlurred.emit(this.field());
  }

  /**
   * Handler for the escape keydown event.
   *
   * @param event Keyboard event.
   */
  onEscapeTypeaheadInput(event: Event): void {
    this.currentlyActiveListValue = undefined;
  }

  /** @inheritdoc */
  onFocus(): void {
    this.fieldFocused.emit(this.field());
  }

  /**
   * Handler for the select change event.
   */
  onSelectionChange(): void {
    if (this.field().multiValue) {
      const mvFieldMenu = this.mvFieldMenu();
      assertExists(
        mvFieldMenu,
        'mvFieldMenu is required for multi-value fields.'
      );
      mvFieldMenu.addMultiValue.emit({ append: true });
    }
  }

  /** @inheritdoc */
  shouldBlur(event: FocusEvent): boolean {
    return super.shouldBlur(event) && !this.autocompleteTrigger.panelOpen;
  }

  private listenForList(): void {
    this.lists
      .get(this.field().list.listId)
      .pipe(untilDestroyed(this))
      .subscribe((list) => {
        this.list = list;
      });
  }
}
